import React, { useMemo } from 'react';
import AntLocalization from '../../utils/AntLocalization';

// ─── Single Resource Row (bên trái timeline) ─────────────────
function ResourceRow({ resource, bookingCount, occupancyPct, showOccupancy, cellHeight }) {
    var name = resource.name || resource.code || '';
    var group = resource.group || resource.tags || '';
    var avatar = resource.avatar || resource.icon || null;

    var rowStyle = cellHeight ? { height: cellHeight + 'px' } : {};

    return (
        <div className="resource-row" style={rowStyle}>
            <div className="resource-info">
                {avatar ? (
                    <div className="resource-avatar">
                        {typeof avatar === 'string' && avatar.length <= 3
                            ? <span className="avatar-text">{avatar}</span>
                            : <img src={avatar} alt="" className="avatar-img" />}
                    </div>
                ) : (
                    <div className="resource-avatar">
                        <span className="avatar-text">{name ? name.charAt(0).toUpperCase() : '?'}</span>
                    </div>
                )}
                <div className="resource-details">
                    <span className="resource-name" title={name}>{name}</span>
                    {group && <span className="resource-group">{typeof group === 'object' ? AntLocalization.translateText(group) : group}</span>}
                </div>
            </div>
            {showOccupancy && occupancyPct != null && (
                <div className="resource-occupancy">
                    <div className="occupancy-bar-track">
                        <div
                            className={'occupancy-bar-fill' + (occupancyPct >= 80 ? ' high' : occupancyPct >= 50 ? ' medium' : '')}
                            style={{ width: Math.min(occupancyPct, 100) + '%' }}
                        />
                    </div>
                    <span className="occupancy-pct">{Math.round(occupancyPct)}%</span>
                </div>
            )}
            {bookingCount > 0 && !showOccupancy && (
                <span className="resource-badge">{bookingCount}</span>
            )}
        </div>
    );
}

// ─── Group Header ─────────────────────────────────────────────
function GroupHeader({ groupName, count, collapsed, onToggle }) {
    var label = typeof groupName === 'object' ? AntLocalization.translateText(groupName) : (groupName || '');
    return (
        <div className={'resource-group-header' + (collapsed ? ' collapsed' : '')} onClick={onToggle}>
            <span className="group-toggle">{collapsed ? '▶' : '▼'}</span>
            <span className="group-name">{label}</span>
            <span className="group-count">({count})</span>
        </div>
    );
}

// ─── Main Panel ───────────────────────────────────────────────
export default function ResourcePanel(props) {
    var {
        resources,
        groupBy,
        bookingCounts,
        occupancyMap,
        showOccupancy,
        collapsedGroups,
        onToggleGroup,
        cellHeight
    } = props;

    // Group resources nếu có groupBy
    var groups = useMemo(function () {
        if (!groupBy || !resources) return null;

        var map = {};
        var order = [];

        for (var i = 0; i < resources.length; i++) {
            var r = resources[i];
            var gKey = r[groupBy] || '';
            if (typeof gKey === 'object') gKey = AntLocalization.translateText(gKey) || '';
            if (!map[gKey]) {
                map[gKey] = [];
                order.push(gKey);
            }
            map[gKey].push(r);
        }

        return order.map(function (key) {
            return { name: key, items: map[key] };
        });
    }, [resources, groupBy]);

    var renderResourceRow = function (resource) {
        var rId = resource.id || resource.code;
        var count = (bookingCounts && bookingCounts[rId]) || 0;
        var occupancy = (occupancyMap && occupancyMap[rId]) || null;

        return (
            <ResourceRow
                key={rId}
                resource={resource}
                bookingCount={count}
                occupancyPct={occupancy}
                showOccupancy={showOccupancy}
                cellHeight={cellHeight}
            />
        );
    };

    if (groups) {
        return (
            <div className="resource-panel">
                <div className="resource-panel-header">
                    <span>{AntLocalization.translateText({ vi: 'Tài nguyên', en: 'Resources', fr: 'Ressources', es: 'Recursos', id: 'Sumber daya', ja: 'リソース', zh: '资源', ko: '리소스', hi: 'संसाधन', ar: 'الموارد' })}</span>
                </div>
                {groups.map(function (group) {
                    var isCollapsed = collapsedGroups && collapsedGroups[group.name];
                    return (
                        <div key={group.name} className="resource-group">
                            <GroupHeader
                                groupName={group.name}
                                count={group.items.length}
                                collapsed={isCollapsed}
                                onToggle={function () { onToggleGroup && onToggleGroup(group.name); }}
                            />
                            {!isCollapsed && group.items.map(renderResourceRow)}
                        </div>
                    );
                })}
            </div>
        );
    }

    return (
        <div className="resource-panel">
            <div className="resource-panel-header">
                <span>{AntLocalization.translateText({ vi: 'Tài nguyên', en: 'Resources', fr: 'Ressources', es: 'Recursos', id: 'Sumber daya', ja: 'リソース', zh: '资源', ko: '리소스', hi: 'संसाधन', ar: 'الموارد' })}</span>
            </div>
            {resources && resources.map(renderResourceRow)}
        </div>
    );
}
